import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import {
  DiscardCloneJobResponse,
  DismissCloneJobResponse,
  ListCloneJobsResponse,
  TenantCloneJob,
  discardCloneJobContract,
  dismissCloneJobContract,
  listCloneJobsContract
} from '@bliks-io/contracts';
import { apiClient } from '@/api/client';
import { tenantKeys } from './useTenants';

/**
 * Query key factory for tenant clone jobs.
 *
 * Deliberately its own root rather than a branch of `tenantKeys`: the tenant
 * family is invalidated wholesale by tenant mutations, and clone jobs are a
 * different resource with a different (polled) refresh rhythm.
 */
export const cloneJobKeys = {
  all: ['cloneJobs'] as const,
  list: () => [...cloneJobKeys.all, 'list'] as const
};

const CLONE_JOB_POLL_INTERVAL_MS = 3_000;

/**
 * Poll the unfinished tenant clone jobs (super-admin only).
 *
 * A separate endpoint from `useListTenants` on purpose: the tenant list is
 * shared by five screens and its service fans out two tenant-database counts
 * per tenant, so polling it every few seconds to watch one clone would be a
 * 2xN fan-out for everybody.
 *
 * A finished clone deletes its own job document, so every job returned here is
 * unfinished business and an empty list is the completion signal — which is why
 * the interval switches itself off once the list comes back empty. A clone that
 * starts while nothing is polling is picked up by `useCloneTenant`, which
 * invalidates this query.
 *
 * A plain query with no cross-query concern of its own, so it is reusable:
 * refreshing the TENANT list when a job disappears belongs to the screen that
 * shows both, not to the hook that fetches one of them.
 */
export function useCloneJobs() {
  return useQuery({
    queryKey: cloneJobKeys.list(),
    queryFn: async ({ signal }): Promise<ListCloneJobsResponse> => {
      const { data } = await apiClient.get<ListCloneJobsResponse>(listCloneJobsContract.path, {
        signal
      });
      return data;
    },
    refetchInterval: (query) => (query.state.data?.jobs.length ? CLONE_JOB_POLL_INTERVAL_MS : false)
  });
}

/**
 * Refresh the tenant list when a clone job LEAVES the poll.
 *
 * A clone flips its tenant to `active: true` and then deletes its job, so a job
 * disappearing is the only client-visible sign that the tenant row itself
 * changed — and the tenant list is deliberately not polled. A job APPEARING
 * needs nothing: `useCloneTenant` already invalidates the list.
 *
 * The previous ids live in state, not a ref written during an effect: under
 * StrictMode a ref-for-prev is written by the discarded first pass, so the
 * second pass compares a value against itself and the transition is missed.
 */
export function useRefreshTenantsWhenACloneJobFinishes(jobs: TenantCloneJob[] | undefined) {
  const queryClient = useQueryClient();
  const openJobIds = useMemo(() => new Set((jobs ?? []).map((job) => job.jobId)), [jobs]);
  const [previousOpenJobIds, setPreviousOpenJobIds] = useState<Set<string> | null>(null);

  if (previousOpenJobIds !== openJobIds) {
    if (
      previousOpenJobIds !== null &&
      [...previousOpenJobIds].some((jobId) => !openJobIds.has(jobId))
    ) {
      void queryClient.invalidateQueries({ queryKey: tenantKeys.lists() });
    }
    setPreviousOpenJobIds(openJobIds);
  }
}

/**
 * Roll back an interrupted or failed clone: revoke memberships, deactivate the
 * Zitadel org, drop the database, delete the tenant row.
 *
 * The only destructive action in the clone lifecycle — the reaper parks jobs
 * and never drops anything. The backend refuses (409
 * `TENANT_CLONE_DISCARD_REFUSED`) whenever a guard says the clone actually
 * completed, so a refusal means nothing was dropped.
 */
export function useDiscardCloneJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (jobId: string): Promise<DiscardCloneJobResponse> => {
      const path = discardCloneJobContract.path.replace(':jobId', jobId);
      const { data } = await apiClient.post<DiscardCloneJobResponse>(path);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cloneJobKeys.all });
      // The discarded clone's tenant row and its per-tenant user lists are gone.
      queryClient.invalidateQueries({ queryKey: tenantKeys.all });
    }
  });
}

/**
 * Clear a clone job without rolling anything back.
 *
 * The acknowledgement path for a clone that succeeded except for its demo
 * Zitadel import: that tenant is kept and active, so a discard is refused by
 * design and dismissing the warning is the only action left. Dismissing is not
 * cancelling — the backend refuses a job that is still running.
 */
export function useDismissCloneJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (jobId: string): Promise<DismissCloneJobResponse> => {
      const path = dismissCloneJobContract.path.replace(':jobId', jobId);
      const { data } = await apiClient.delete<DismissCloneJobResponse>(path);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: cloneJobKeys.all });
    }
  });
}
